import { useState, useRef, useEffect, useMemo } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { Modal } from '@klear/ui/components/modal'
import { Button } from '@klear/ui/components/button'
import { Label } from '@klear/ui/components/label'
import { Input } from '@klear/ui/components/input'
import { Checkbox } from '@klear/ui/components/checkbox'
import { Alert, AlertDescription, AlertTitle } from '@klear/ui/components/alert'
import { AlertCircle } from 'lucide-react'
import SignatureCanvas from 'react-signature-canvas'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { buildLegalDocumentFileUrl, withAttachmentDisposition } from '@klear/upload/file-url'
import { LegalAgreement } from '@klear/legal-agreement'
import {
  useGeneratedLegalAgreementMutations,
  useLegalAgreementAcceptanceMutations
} from '@/app/legal-agreement/use-legal-agreement'
import { E_SIGNATURE_CONSENT_TEXT, ESIGN_CONSENT_EVENT_CHECKED, ESIGN_CONSENT_EVENT_UNCHECKED } from '@klear/constants'
import { useFileDownload } from '@klear/ui/lib/download-file'
import { trpc, type RouterOutputs } from '@klear/ui/lib/trpc'
import isArray from 'lodash/isArray'
import isError from 'lodash/isError'
import times from 'lodash/times'

interface GeneratedLegalAgreementViewerProps {
  agreement?: LegalAgreement | null
  traderId: number
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  externalAccountIds?: number[]
  klearAccountIds?: number[]
  accountId?: number
  accountType?: 'external' | 'klear'
  hideOverlay?: boolean
}

type GeneratedDocument = {
  htmlContent: string
  documentHash: string
  documentContentUrl: string
  documentPresentedAt: string
  unsignedPdfHash: string | null
  unsignedPdfUrl: string | null
  signingSessionId?: number
}

type SignerForm = {
  firstName: string
  lastName: string
  email: string
  title: string
  hasConsent: boolean
}

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

function makeEmptySignerForm(email: string): SignerForm {
  return { firstName: '', lastName: '', email, title: '', hasConsent: false }
}

function isSignerFormValid(form: SignerForm, signatureRequired: boolean): boolean {
  const baseValid = EMAIL_PATTERN.test(form.email.trim()) && form.title.trim() !== '' && form.hasConsent
  if (!signatureRequired) return baseValid
  return baseValid && form.firstName.trim() !== '' && form.lastName.trim() !== ''
}

type AccountIds = { klearAccountIds: number[]; externalAccountIds: number[] }

type GenerateDocumentResult = RouterOutputs['legalAgreement']['generateDynamicDocument']

function parseAccountIdsFromResponse(data: GenerateDocumentResult): AccountIds {
  if (isArray(data.klearAccountIds) || isArray(data.externalAccountIds)) {
    return {
      klearAccountIds: data.klearAccountIds ?? [],
      externalAccountIds: data.externalAccountIds ?? []
    }
  }
  if (data.htmlContent) {
    try {
      const match = data.htmlContent.match(/<script[^>]*id=["']accounts-data-json["'][^>]*>([\s\S]*?)<\/script>/i)
      if (match?.[1]) {
        const parsed = JSON.parse(match[1].trim()) as { klearAccountIds?: number[]; externalAccountIds?: number[] }
        return {
          klearAccountIds: parsed.klearAccountIds ?? [],
          externalAccountIds: parsed.externalAccountIds ?? []
        }
      }
    } catch (err) {
      console.warn('Failed to extract account IDs from document:', err)
    }
  }
  return { klearAccountIds: [], externalAccountIds: [] }
}

function buildPdfFileUrl(storageKey: string, traderId: number): string {
  return buildLegalDocumentFileUrl(traderId, storageKey)
}

async function fetchPdfAsBlobUrl(url: string): Promise<string> {
  const response = await fetch(url, { credentials: 'include', mode: 'cors' })
  if (!response.ok) throw new Error(`Failed to fetch PDF: ${response.statusText}`)
  const blob = await response.blob()
  const pdfBlob = blob.type?.includes('pdf') ? blob : new Blob([blob], { type: 'application/pdf' })
  return URL.createObjectURL(pdfBlob)
}

export function GeneratedLegalAgreementViewer({
  agreement: agreementProp,
  traderId,
  open,
  onClose,
  onSuccess,
  externalAccountIds = [],
  klearAccountIds = [],
  hideOverlay = false
}: GeneratedLegalAgreementViewerProps) {
  const { user } = useAuth()
  const { startDownload, isDownloading } = useFileDownload()
  const signatureRef = useRef<SignatureCanvas>(null)
  const [document, setDocument] = useState<GeneratedDocument | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const [hasSignature, setHasSignature] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pdfLoadError, setPdfLoadError] = useState(false)
  const [accountIds, setAccountIds] = useState<AccountIds | null>(null)
  const [numPages, setNumPages] = useState<number | null>(null)

  const containerRef = useRef<HTMLDivElement>(null)
  const [pageWidth, setPageWidth] = useState(600)

  const fetchAchTemplate = open && !agreementProp && (externalAccountIds.length > 0 || klearAccountIds.length > 0)
  const {
    data: achTemplateData,
    isLoading: isLoadingAchTemplate,
    isFetched: achTemplateFetched,
    error: achTemplateQueryError
  } = trpc.legalAgreement.getAchAgreementTemplate.useQuery(undefined, {
    enabled: fetchAchTemplate,
    staleTime: 0,
    refetchOnMount: 'always'
  })

  const achTemplateMissing = fetchAchTemplate && achTemplateFetched && achTemplateData == null && !achTemplateQueryError

  const agreement = useMemo(() => {
    if (agreementProp) return agreementProp
    if (achTemplateData) return achTemplateData
    return null
  }, [agreementProp, achTemplateData])

  const { generateDocument, uploadSignedPdfAndCreateAcceptance, recordSigningSessionEvent } =
    useGeneratedLegalAgreementMutations()
  const { createTraderLegalAgreementAcceptance } = useLegalAgreementAcceptanceMutations()
  const documentViewedEventSent = useRef(false)
  const generationStartedRef = useRef(false)

  const [form, setForm] = useState<SignerForm>(() => makeEmptySignerForm(user?.email || ''))

  const setField = <K extends keyof SignerForm>(key: K, value: SignerForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  useEffect(() => {
    if (user?.email) setForm((prev) => (prev.email === '' ? { ...prev, email: user.email } : prev))
  }, [user?.email])

  const isFormValid = isSignerFormValid(form, agreement?.signatureRequired ?? false)

  const pdfDisplayUrl = pdfBlobUrl ?? pdfUrl

  const openPdfInNewWindow = () => {
    if (pdfDisplayUrl) window.open(pdfDisplayUrl, '_blank', 'noopener,noreferrer')
  }

  const downloadHref = pdfBlobUrl ?? (pdfUrl === null ? null : withAttachmentDisposition(pdfUrl))

  const downloadPdf = () => {
    if (downloadHref !== null) {
      startDownload(downloadHref, 'agreement.pdf')
    }
  }

  useEffect(() => {
    if (!open) {
      generationStartedRef.current = false
      return
    }
    if (!agreement || document) return
    if (generationStartedRef.current) return
    generationStartedRef.current = true

    const variables = {
      legalAgreementId: agreement.id,
      externalAccountIds,
      klearAccountIds
    }

    generateDocument(variables)
      .then(async (data) => {
        setDocument({
          htmlContent: data.htmlContent || '',
          documentHash: data.documentHash,
          documentContentUrl: data.documentContentUrl,
          documentPresentedAt: data.documentPresentedAt,
          unsignedPdfHash: data.unsignedPdfHash || null,
          unsignedPdfUrl: data.unsignedPdfUrl || null,
          ...(data.signingSessionId != null && { signingSessionId: data.signingSessionId })
        })
        setAccountIds(parseAccountIdsFromResponse(data))

        if (!data.unsignedPdfUrl) {
          setPdfLoadError(true)
          setError('PDF document is not available. Cannot proceed with signing.')
          return
        }

        const pdfFullUrl = buildPdfFileUrl(data.unsignedPdfUrl, traderId)
        setPdfUrl(pdfFullUrl)
        setPdfLoadError(false)
        setError(null)
        setNumPages(null)

        try {
          const blobUrl = await fetchPdfAsBlobUrl(pdfFullUrl)
          setPdfBlobUrl(blobUrl)
          setPdfLoadError(false)
          setError(null)
        } catch (err) {
          console.error('Failed to load PDF:', err)
          setPdfLoadError(true)
          setError(`Failed to load PDF document: ${isError(err) ? err.message : 'Unknown error'}`)
        }
      })
      .catch((error) => {
        console.error('Failed to generate document:', error)
        setError(isError(error) ? error.message : 'Failed to generate document. Please try again.')
        setPdfLoadError(true)
      })
  }, [open, agreement, document, generateDocument, traderId, externalAccountIds, klearAccountIds])

  useEffect(() => {
    if (typeof window !== 'undefined' && pdfjs.GlobalWorkerOptions) {
      pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current || !pdfBlobUrl) return
    const el = containerRef.current

    const compute = () => {
      const w = el.clientWidth
      const next = Math.max(320, Math.min(w - 32, 900))
      setPageWidth(next)
    }

    const observer = new ResizeObserver(() => compute())
    observer.observe(el)
    compute()

    return () => observer.disconnect()
  }, [pdfBlobUrl])

  useEffect(() => {
    if (!agreement?.signatureRequired || !document?.signingSessionId || documentViewedEventSent.current) return
    documentViewedEventSent.current = true
    recordSigningSessionEvent({
      signingSessionId: document.signingSessionId,
      legalAgreementId: agreement.id,
      eventType: 'document_viewed',
      payload: { user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined }
    }).catch((err) => console.warn('Failed to record document_viewed event:', err))
  }, [agreement, document?.signingSessionId, document, recordSigningSessionEvent])

  const consentDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pendingConsentRef = useRef<boolean | null>(null)
  const lastSentConsentRef = useRef<boolean | null>(null)

  const sendConsentEvent = useMemo(() => {
    return (checked: boolean) => {
      if (!agreement || document?.signingSessionId == null) return Promise.resolve()
      return recordSigningSessionEvent({
        signingSessionId: document.signingSessionId!,
        legalAgreementId: agreement.id,
        eventType: checked ? ESIGN_CONSENT_EVENT_CHECKED : ESIGN_CONSENT_EVENT_UNCHECKED,
        payload: {
          checked,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
        }
      }).catch((err) => {
        console.warn('Failed to record consent_change event:', err)
      })
    }
  }, [agreement, document?.signingSessionId, recordSigningSessionEvent])

  const handleConsentChange = (checked: boolean) => {
    if (!agreement || document?.signingSessionId == null) return
    pendingConsentRef.current = checked
    if (consentDebounceRef.current) clearTimeout(consentDebounceRef.current)
    consentDebounceRef.current = setTimeout(() => {
      consentDebounceRef.current = null
      const toSend = pendingConsentRef.current
      pendingConsentRef.current = null
      if (toSend === null) return
      if (lastSentConsentRef.current === null && toSend === false) return
      if (toSend === lastSentConsentRef.current) return
      lastSentConsentRef.current = toSend
      sendConsentEvent(toSend)
    }, 400)
  }

  const flushConsentEventBeforeSign = (currentChecked: boolean) => {
    if (consentDebounceRef.current) {
      clearTimeout(consentDebounceRef.current)
      consentDebounceRef.current = null
      pendingConsentRef.current = null
    }
    if (currentChecked !== lastSentConsentRef.current) {
      lastSentConsentRef.current = currentChecked
      return sendConsentEvent(currentChecked)
    }
    return Promise.resolve()
  }

  const handleSignatureEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) setHasSignature(true)
  }

  const handleClearSignature = () => {
    signatureRef.current?.clear()
    setHasSignature(false)
  }

  const handleClose = () => {
    if (consentDebounceRef.current) {
      clearTimeout(consentDebounceRef.current)
      consentDebounceRef.current = null
      pendingConsentRef.current = null
    }
    lastSentConsentRef.current = null
    documentViewedEventSent.current = false
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl)
      setPdfBlobUrl(null)
    }
    setDocument(null)
    setPdfUrl(null)
    setNumPages(null)
    setHasSignature(false)
    setError(null)
    setPdfLoadError(false)
    setAccountIds(null)
    signatureRef.current?.clear()
    setForm(makeEmptySignerForm(user?.email || ''))
    onClose()
  }

  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl)
    }
  }, [pdfBlobUrl])

  const handleSubmit = async () => {
    if (!agreement) return
    if (!isFormValid) return
    if (!document) {
      setError('Document not loaded. Please try again.')
      return
    }
    if (!pdfUrl || pdfLoadError) {
      setError('PDF document is not available. Cannot proceed with signing.')
      return
    }
    if (document.signingSessionId == null) {
      setError('Something went wrong. Please close and reopen the agreement to try again.')
      return
    }
    const signingSessionId = document.signingSessionId

    await flushConsentEventBeforeSign(form.hasConsent)

    const isSignatureRequired = agreement.signatureRequired
    if (isSignatureRequired && (!hasSignature || !signatureRef.current)) {
      setError('Please provide a signature')
      return
    }

    setIsSubmitting(true)
    try {
      if (isSignatureRequired) {
        const canvas = signatureRef.current!.getCanvas()
        const signature = canvas.toDataURL()

        const signerDataForStorage: {
          firstName: string
          lastName: string
          email: string
          title: string
          [key: string]: unknown
        } = {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          title: form.title,
          eSignatureConsent: true
        }

        const documentHash = document.unsignedPdfHash || document.documentHash
        const consentText = agreement.esignConsentText ?? E_SIGNATURE_CONSENT_TEXT
        const userAgent = navigator.userAgent

        await uploadSignedPdfAndCreateAcceptance({
          traderId,
          documentHash,
          legalAgreementId: agreement.id,
          signature,
          signerData: signerDataForStorage,
          documentContentUrl: document.documentContentUrl,
          documentPresentedAt: document.documentPresentedAt,
          consentText,
          userAgent,
          signingSessionId,
          klearAccountIds: accountIds?.klearAccountIds,
          externalAccountIds: accountIds?.externalAccountIds
        })
      } else {
        const signerDataForCheckbox: {
          firstName: string
          lastName: string
          email: string
          title: string
          [key: string]: unknown
        } = {
          firstName: '',
          lastName: '',
          email: form.email,
          title: form.title,
          eSignatureConsent: true
        }

        const consentText = agreement.esignConsentText ?? E_SIGNATURE_CONSENT_TEXT
        await createTraderLegalAgreementAcceptance({
          legalAgreementId: agreement.id,
          signingSessionId,
          signerData: signerDataForCheckbox,
          consentText,
          klearAccountIds: accountIds?.klearAccountIds,
          externalAccountIds: accountIds?.externalAccountIds
        })
      }

      onSuccess?.()
      handleClose()
    } catch (err) {
      console.error('Failed to submit acceptance:', err)
      setError(isError(err) ? err.message : 'Failed to submit acceptance. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!open) return null

  const showSingleModal = open && (agreement != null || fetchAchTemplate)
  if (!showSingleModal && open && !agreement) return null

  const modalTitle = agreement?.title ?? 'ACH Agreement'

  return (
    <Modal
      title={modalTitle}
      open={open}
      disableOutsideClick={true}
      hideOverlay={hideOverlay}
      onOpenChange={(isOpen) => {
        if (!isOpen) handleClose()
      }}
    >
      {!agreement ? (
        <div className="flex flex-col items-center justify-center gap-3 p-8 text-center">
          {isLoadingAchTemplate ? (
            <p>Loading ACH agreement...</p>
          ) : achTemplateQueryError ? (
            <>
              <Alert variant="destructive" className="text-left">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>
                  {isError(achTemplateQueryError)
                    ? achTemplateQueryError.message
                    : 'Failed to load ACH agreement template.'}
                </AlertDescription>
              </Alert>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </>
          ) : achTemplateMissing ? (
            <>
              <Alert variant="destructive" className="text-left">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>ACH agreement unavailable</AlertTitle>
                <AlertDescription>
                  No ACH agreement template is configured in the database. Local dev usually needs{' '}
                  <code className="text-xs">legal_agreement</code> row with name{' '}
                  <code className="text-xs">ach_agreement</code> and <code className="text-xs">is_dynamic=true</code>{' '}
                  (see <code className="text-xs">apps/server/src/db/seed.ts</code>).
                </AlertDescription>
              </Alert>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </>
          ) : (
            <p>Loading ACH agreement...</p>
          )}
        </div>
      ) : (
        <>
          {pdfUrl && (
            <div className="flex justify-end m-0">
              <Button
                variant="link"
                size="sm"
                className="text-blue-500 hover:text-blue-700"
                onClick={openPdfInNewWindow}
              >
                Open in New Window
              </Button>
            </div>
          )}
          <div className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {agreement && !document && !error ? (
              <div className="flex items-center justify-center p-8">
                <p>Generating document...</p>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  void handleSubmit()
                }}
                className="space-y-4"
              >
                <div className="border rounded-lg bg-card shadow-sm w-full overflow-hidden">
                  {pdfUrl ? (
                    <div className="space-y-2">
                      <div
                        ref={containerRef}
                        className="w-full max-h-[50vh] overflow-auto bg-muted p-4 border rounded-lg"
                        style={{ minHeight: '400px' }}
                      >
                        {pdfLoadError && (
                          <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 space-y-4">
                            <div className="text-center space-y-2">
                              <p className="text-lg font-semibold">PDF Document Ready</p>
                              <p className="text-sm text-muted-foreground">
                                Your browser&apos;s security settings prevent displaying PDFs inline. Please open the
                                document in a new window to view and sign it.
                              </p>
                            </div>
                            <div className="flex gap-3">
                              <Button onClick={openPdfInNewWindow} className="px-6">
                                Open PDF in New Window
                              </Button>
                              <Button variant="outline" onClick={downloadPdf} disabled={isDownloading}>
                                {isDownloading ? 'Downloading…' : 'Download PDF'}
                              </Button>
                            </div>
                          </div>
                        )}

                        {!pdfLoadError && pdfBlobUrl && (
                          <Document
                            file={pdfBlobUrl}
                            loading={
                              <div className="flex items-center justify-center min-h-[200px] py-8">
                                <p>Loading PDF...</p>
                              </div>
                            }
                            error={
                              <div className="flex flex-col items-center justify-center min-h-[200px] p-4 text-center">
                                <p className="text-destructive font-medium">Failed to load PDF.</p>
                                <Button variant="outline" size="sm" className="mt-2" onClick={openPdfInNewWindow}>
                                  Open in New Window
                                </Button>
                              </div>
                            }
                            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                            onLoadError={() => setPdfLoadError(true)}
                            className={[
                              'w-full',
                              'flex flex-col items-center',
                              '[&_.react-pdf__Page__canvas]:block',
                              '[&_.react-pdf__Page]:m-0',
                              '[&_.react-pdf__Page__textContent]:m-0',
                              '[&_.react-pdf__Page__annotations]:m-0'
                            ].join(' ')}
                          >
                            {numPages != null &&
                              times(numPages, (i) => (
                                <div
                                  key={`page_wrap_${i + 1}`}
                                  className="mb-4 rounded-xl bg-white shadow-md ring-1 ring-black/5 overflow-hidden"
                                >
                                  <Page
                                    pageNumber={i + 1}
                                    width={pageWidth}
                                    renderTextLayer={true}
                                    renderAnnotationLayer={true}
                                    className="p-0"
                                  />
                                </div>
                              ))}
                          </Document>
                        )}

                        {!pdfLoadError && !pdfBlobUrl && (
                          <div className="flex items-center justify-center min-h-[400px]">
                            <p>Loading PDF...</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center p-8" style={{ minHeight: '400px' }}>
                      <p>Loading document...</p>
                    </div>
                  )}
                </div>

                <div className="space-y-4 border-t pt-4">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="esign-consent"
                      checked={form.hasConsent}
                      disabled={isSubmitting}
                      onCheckedChange={(checked) => {
                        const next = checked === true
                        setField('hasConsent', next)
                        handleConsentChange(next)
                      }}
                    />
                    <Label htmlFor="esign-consent" className="cursor-pointer text-sm leading-5">
                      {agreement.esignConsentText ?? E_SIGNATURE_CONSENT_TEXT}
                    </Label>
                  </div>

                  {agreement.signatureRequired ? (
                    <>
                      <h3 className="font-semibold">Signer Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label htmlFor="signer-first-name">First Name</Label>
                          <Input
                            id="signer-first-name"
                            value={form.firstName}
                            onChange={(e) => setField('firstName', e.target.value)}
                            placeholder="Enter first name"
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="signer-last-name">Last Name</Label>
                          <Input
                            id="signer-last-name"
                            value={form.lastName}
                            onChange={(e) => setField('lastName', e.target.value)}
                            placeholder="Enter last name"
                            disabled={isSubmitting}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="signer-email">Email</Label>
                        <Input
                          id="signer-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => setField('email', e.target.value)}
                          placeholder="Enter email"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="signer-title">Title</Label>
                        <Input
                          id="signer-title"
                          value={form.title}
                          onChange={(e) => setField('title', e.target.value)}
                          placeholder="e.g. CEO, Authorized Signatory"
                          disabled={isSubmitting}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Signature</Label>
                        <div className={`border rounded-lg ${isSubmitting ? 'pointer-events-none opacity-60' : ''}`}>
                          <SignatureCanvas
                            ref={signatureRef}
                            onEnd={handleSignatureEnd}
                            penColor="#000000"
                            canvasProps={{
                              className: 'w-full h-48 rounded-lg bg-white',
                              style: { touchAction: 'none' }
                            }}
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleClearSignature}
                            className="flex-1"
                            disabled={isSubmitting}
                          >
                            Clear Signature
                          </Button>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" onClick={handleClose} type="button" className="flex-1">
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={
                            !document || !pdfUrl || pdfLoadError || !isFormValid || !hasSignature || isSubmitting
                          }
                          className="flex-1"
                        >
                          {isSubmitting ? 'Submitting...' : 'Sign and Submit'}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-1">
                        <Label htmlFor="checkbox-email">Email (for audit trail)</Label>
                        <Input
                          id="checkbox-email"
                          type="email"
                          value={form.email}
                          onChange={(e) => setField('email', e.target.value)}
                          placeholder="Enter email"
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="checkbox-title">Title</Label>
                        <Input
                          id="checkbox-title"
                          value={form.title}
                          onChange={(e) => setField('title', e.target.value)}
                          placeholder="e.g. CEO, Authorized Signatory"
                          disabled={isSubmitting}
                        />
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" onClick={handleClose} type="button" className="flex-1">
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={!document || !pdfUrl || pdfLoadError || !isFormValid || isSubmitting}
                          className="flex-1"
                        >
                          {isSubmitting ? 'Submitting...' : 'Accept and Submit'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </form>
            )}
          </div>
        </>
      )}
    </Modal>
  )
}
